/**
 * Cliente Form Dialog - Create/Edit Client
 * Formulario para crear o editar clientes del laboratorio.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useApiError } from "@/hooks/use-api-error";
import { useCreateCliente, useUpdateCliente, type Cliente } from "@/hooks/use-clientes";
import { TIPOS_CLIENTE } from "@/hooks/use-clientes";
import { useSucursal } from "@/contexts/sucursal-context";
import { useListasPrecios } from "@/hooks/use-facturacion";

interface ClienteFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cliente: Cliente | null;
  onSuccess: (isNew: boolean) => void;
}

const clienteSchema = z.object({
  nombre: z.string().min(2, "clientes.validation.nameRequired"),
  tipo: z.string().optional(),
  codigo: z.string().optional(),
  rtn: z
    .string()
    .optional()
    .refine(
      (val) => !val || val === "" || /^\d{4}-\d{4}-\d{6}$/.test(val),
      "clientes.validation.rtnInvalid"
    ),
  contacto_nombre: z.string().optional(),
  contacto_telefono: z.string().optional(),
  contacto_email: z.string().email("clientes.validation.emailInvalid").optional().or(z.literal("")),
  direccion: z.string().optional(),
  observaciones: z.string().optional(),
  lista_precio: z.string().optional(),
});

type FormData = z.infer<typeof clienteSchema>;

export function ClienteFormDialog({
  open,
  onOpenChange,
  cliente,
  onSuccess,
}: ClienteFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const { sucursalActiva } = useSucursal();
  const [error, setError] = useState<string | null>(null);
  const { data: listasPrecios } = useListasPrecios(true);

  const isEditing = !!cliente;

  const createMutation = useCreateCliente({
    onSuccess: () => onSuccess(true),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const updateMutation = useUpdateCliente({
    onSuccess: () => onSuccess(false),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(clienteSchema),
    defaultValues: {
      nombre: "",
      tipo: "PARTICULAR",
      codigo: "",
      rtn: "",
      contacto_nombre: "",
      contacto_telefono: "",
      contacto_email: "",
      direccion: "",
      observaciones: "",
      lista_precio: "",
    },
  });

  useEffect(() => {
    if (open && cliente) {
      reset({
        nombre: cliente.nombre,
        tipo: cliente.tipo || "PARTICULAR",
        codigo: cliente.codigo || "",
        rtn: cliente.rtn || "",
        contacto_nombre: cliente.contacto_nombre || "",
        contacto_telefono: cliente.contacto_telefono || "",
        contacto_email: cliente.contacto_email || "",
        direccion: cliente.direccion || "",
        observaciones: cliente.observaciones || "",
        lista_precio: cliente.lista_precio ? String(cliente.lista_precio) : "",
      });
      setError(null);
    } else if (open && !cliente) {
      reset({
        nombre: "",
        tipo: "PARTICULAR",
        codigo: "",
        rtn: "",
        contacto_nombre: "",
        contacto_telefono: "",
        contacto_email: "",
        direccion: "",
        observaciones: "",
        lista_precio: "",
      });
      setError(null);
    }
  }, [open, cliente, reset]);

  const onSubmit = (data: FormData) => {
    setError(null);

    const sucursalId = sucursalActiva && sucursalActiva.id > 0 ? sucursalActiva.id : 0;

    const payload = {
      nombre: data.nombre,
      tipo: (data.tipo as "AREA_HOSPITALARIA" | "EMPRESA" | "PARTICULAR") || undefined,
      codigo: data.codigo || undefined,
      rtn: data.rtn || undefined,
      contacto_nombre: data.contacto_nombre || undefined,
      contacto_telefono: data.contacto_telefono || undefined,
      contacto_email: data.contacto_email || undefined,
      direccion: data.direccion || undefined,
      observaciones: data.observaciones || undefined,
      sucursal: sucursalId,
      lista_precio: data.lista_precio ? parseInt(data.lista_precio, 10) : null,
    };

    if (isEditing && cliente) {
      updateMutation.mutate({ id: cliente.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t("clientes.editClient") : t("clientes.newClient")}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t("clientes.editClientDesc") : t("clientes.newClientDesc")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          <div className="grid gap-4">
            {/* Nombre y Tipo */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">{t("clientes.fields.name")} *</Label>
                <Input
                  id="nombre"
                  placeholder={isEditing ? undefined : t("clientes.fields.namePlaceholder")}
                  className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                  {...register("nombre")}
                />
                {errors.nombre && (
                  <p className="text-sm text-destructive">{t(errors.nombre.message as string)}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="tipo">{t("clientes.fields.type")}</Label>
                <Select
                  value={watch("tipo") || "PARTICULAR"}
                  onValueChange={(value) => setValue("tipo", value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_CLIENTE.map((tipo) => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Código y RTN */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="codigo">{t("clientes.fields.code")}</Label>
                <Input
                  id="codigo"
                  placeholder={t("clientes.fields.codePlaceholder")}
                  className="placeholder:text-muted-foreground/50"
                  {...register("codigo")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rtn">{t("clientes.fields.rtn")}</Label>
                <Input
                  id="rtn"
                  placeholder={isEditing ? undefined : "0000-0000-000000"}
                  className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                  {...register("rtn")}
                  onChange={(e) => {
                    // Auto-format RTN: DDDD-DDDD-DDDDDD
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 14);
                    let formatted = digits;
                    if (digits.length > 4) formatted = digits.slice(0, 4) + "-" + digits.slice(4);
                    if (digits.length > 8)
                      formatted = formatted.slice(0, 9) + "-" + digits.slice(8);
                    setValue("rtn", formatted, { shouldValidate: true });
                  }}
                />
                {errors.rtn && (
                  <p className="text-sm text-destructive">{t(errors.rtn.message as string)}</p>
                )}
              </div>
            </div>

            {/* Lista de precios */}
            <div className="space-y-2">
              <Label htmlFor="lista_precio">{t("clientes.fields.listaPrecio")}</Label>
              <Select
                value={watch("lista_precio") || "none"}
                onValueChange={(value) => setValue("lista_precio", value === "none" ? "" : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("clientes.fields.listaPrecioPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("clientes.fields.sinListaPrecio")}</SelectItem>
                  {listasPrecios?.map((lp) => (
                    <SelectItem key={lp.id} value={String(lp.id)}>
                      {lp.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Contacto */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contacto_nombre">{t("clientes.fields.contactName")}</Label>
                <Input id="contacto_nombre" {...register("contacto_nombre")} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contacto_telefono">{t("clientes.fields.contactPhone")}</Label>
                <Input
                  id="contacto_telefono"
                  placeholder={isEditing ? undefined : "+504 0000-0000"}
                  className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                  {...register("contacto_telefono")}
                />
              </div>
            </div>

            {/* Email contacto */}
            <div className="space-y-2">
              <Label htmlFor="contacto_email">{t("clientes.fields.contactEmail")}</Label>
              <Input
                id="contacto_email"
                type="email"
                placeholder={isEditing ? undefined : "contacto@empresa.com"}
                className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                {...register("contacto_email")}
              />
              {errors.contacto_email && (
                <p className="text-sm text-destructive">
                  {t(errors.contacto_email.message as string)}
                </p>
              )}
            </div>

            {/* Dirección */}
            <div className="space-y-2">
              <Label htmlFor="direccion">{t("clientes.fields.address")}</Label>
              <Input id="direccion" {...register("direccion")} />
            </div>

            {/* Observaciones */}
            <div className="space-y-2">
              <Label htmlFor="observaciones">{t("clientes.fields.observations")}</Label>
              <Textarea id="observaciones" rows={2} {...register("observaciones")} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
